import React, { useEffect, useState, useContext, useMemo } from "react"
import Header from "../../layouts/Header"
import BackIcon from "../../components/BackIcon"
import {
  Box,
  Paper,
  TextField,
  Tooltip,
  IconButton,
  Grid,
  Typography,
  Autocomplete,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  Button,
} from "@mui/material"
import { createTheme, ThemeProvider } from "@mui/material/styles"
import CustomBackdrop from "../../components/CustomBackdrop"
import { useMutation, useQuery, api, showWarning } from "../../api"
import { GlobalContext } from "../../contexts/GlobalContext"
import getIconComponent from "../utils/getIconComponent"
import Save from "@mui/icons-material/Save"
import SearchIcon from "@mui/icons-material/Search"
import { useQueryClient } from "@tanstack/react-query" // 💡 IMPORTAMOS EL CLIENTE DE REACT QUERY[cite: 3]

const theme = createTheme({
  palette: { primary: { main: "#196C87" }, secondary: { main: "#2E7D32" } },
})

const StyledRoot = {
  width: "100%",
  maxWidth: "1400px",
  margin: "0 auto",
  padding: "20px",
  backgroundColor: "#f5f7fa",
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
}

const AsignacionTipoSolaUsu = () => {
  const qc = useQueryClient() // 💡 INICIALIZAMOS EL CLIENTE PARA MANEJAR LA CACHÉ[cite: 3]
  const { selectedMenuInfo } = useContext(GlobalContext)

  // Estados Principales
  const [selectedUser, setSelectedUser] = useState(null)

  // Listas de Transferencia Locales
  const [leftItems, setLeftItems] = useState([]) // Caja Izquierda (Disponibles)
  const [rightItems, setRightItems] = useState([]) // Caja Derecha (Asignados)
  const [checkedLeft, setCheckedLeft] = useState([])
  const [checkedRight, setCheckedRight] = useState([])

  // Búsqueda con Debounce (solo para la izquierda)
  const [searchTerm, setSearchTerm] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm)
    }, 500)
    return () => clearTimeout(timer)
  }, [searchTerm])

  // =====================================================================
  // QUERIES DE BASE DE DATOS
  // =====================================================================

  // 1. Obtener Usuarios Activos
  const { data: rawUsuarios, isLoading: isLoadUsuarios } = useQuery({
    queryKey: ["getUsuariosActivosAsigSol"],
    queryFn: async () => {
      try {
        const response = await api.get("/AsignacionTipoSolaUsu/getUsuariosActivos")
        return response?.data?.data || response?.data || response || []
      } catch (error) {
        return []
      }
    },
  })

  const usuariosValidados = Array.isArray(rawUsuarios)
    ? rawUsuarios
    : Array.isArray(rawUsuarios?.data)
      ? rawUsuarios.data
      : []
  const listaUsuarios = usuariosValidados.filter((user) => user.usrstatus === "A" || user.usrstatus === "D")

  // 2. Obtener Tipos DISPONIBLES (Caja Izquierda)
  const { data: disponiblesData, isLoading: isLoadDisponibles } = useQuery({
    queryKey: ["getListaTipoSolDisponible", selectedUser?.usrcodigo],
    queryFn: async () => {
      if (!selectedUser) return []
      const res = await api.post("/AsignacionTipoSolaUsu/getListaTipoSolDisponible", {
        usrcodigo: selectedUser.usrcodigo,
      })
      const extract = res?.data?.data?.data || res?.data?.data || res?.data || []
      return Array.isArray(extract) ? extract : []
    },
    enabled: !!selectedUser,
  })

  // 3. Obtener Tipos ASIGNADOS (Caja Derecha)
  const { data: asignadosData, isLoading: isLoadAsignados } = useQuery({
    queryKey: ["getListaTipoSolAsig", selectedUser?.usrcodigo],
    queryFn: async () => {
      if (!selectedUser) return []
      const res = await api.post("/AsignacionTipoSolaUsu/getListaTipoSolAsig", {
        usrcodigo: selectedUser.usrcodigo,
      })
      const extract = res?.data?.data?.data || res?.data?.data || res?.data || []
      return Array.isArray(extract) ? extract : []
    },
    enabled: !!selectedUser,
  })

  // =====================================================================
  // SINCRONIZACIÓN PERFECTA: De React Query al Estado Local
  // =====================================================================
  useEffect(() => {
    if (disponiblesData) {
      setLeftItems(disponiblesData)
    }
  }, [disponiblesData])

  useEffect(() => {
    if (asignadosData) {
      setRightItems(asignadosData)
    }
  }, [asignadosData])

  // =====================================================================
  // LÓGICA DE TRANSFER LIST Y BÚSQUEDA
  // =====================================================================
  const displayedLeftItems = useMemo(() => {
    return leftItems.filter((tipo) => {
      return (
        tipo.tsolcodigo.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
        tipo.tsoldescripcion.toLowerCase().includes(debouncedSearch.toLowerCase())
      )
    })
  }, [leftItems, debouncedSearch])

  const handleToggle = (value, isLeft) => () => {
    const currentChecked = isLeft ? checkedLeft : checkedRight
    const setChecked = isLeft ? setCheckedLeft : setCheckedRight
    const currentIndex = currentChecked.indexOf(value)
    const newChecked = [...currentChecked]

    if (currentIndex === -1) newChecked.push(value)
    else newChecked.splice(currentIndex, 1)

    setChecked(newChecked)
  }

  const moveRight = () => {
    const itemsToMove = leftItems.filter((t) => checkedLeft.includes(t.tsolcodigo))
    setRightItems((prev) => [...prev, ...itemsToMove].sort((a, b) => a.tsolcodigo.localeCompare(b.tsolcodigo)))
    setLeftItems((prev) => prev.filter((t) => !checkedLeft.includes(t.tsolcodigo)))
    setCheckedLeft([])
  }

  const moveLeft = () => {
    const itemsToMove = rightItems.filter((t) => checkedRight.includes(t.tsolcodigo))
    setLeftItems((prev) => [...prev, ...itemsToMove].sort((a, b) => a.tsolcodigo.localeCompare(b.tsolcodigo)))
    setRightItems((prev) => prev.filter((t) => !checkedRight.includes(t.tsolcodigo)))
    setCheckedRight([])
  }

  // =====================================================================
  // MUTACIÓN PARA GRABAR
  // =====================================================================
  const { mutateAsync: SaveAsignacion, isPending: isSaving } = useMutation({
    queryKey: ["isSavingAsignacionSoli"],
    fn: async (payload) => {
      const response = await api.post("/AsignacionTipoSolaUsu/createAsigTipoSol", payload)
      return response.data
    },
    showError: "modal",
    showSuccess: "toast",
    onSuccess: async () => {
      // 💡 SOLUCIÓN: Limpiamos la caché para que el backend se consulte obligatoriamente la próxima vez[cite: 3]
      await qc.invalidateQueries({ queryKey: ["getListaTipoSolDisponible"] })
      await qc.invalidateQueries({ queryKey: ["getListaTipoSolAsig"] })

      // RESET TOTAL AL GRABAR
      setSelectedUser(null)
      setLeftItems([])
      setRightItems([])
      setCheckedLeft([])
      setCheckedRight([])
      setSearchTerm("")
    },
  })

  const handleSubmit = async (e) => {
    if (e) e.preventDefault()
    if (!selectedUser) {
      return showWarning("Debe seleccionar un Usuario primero.")
    }

    const payload = {
      usrcodigo: selectedUser.usrcodigo,
      asignaciones: rightItems.map((item) => item.tsolcodigo),
    }

    try {
      await SaveAsignacion(payload)
    } catch (error) {
      console.error("Error al guardar la asignación:", error)
    }
  }

  // =====================================================================
  // RENDERIZADO DE BARRA SUPERIOR (TOP BAR)
  // =====================================================================
  const grabarAction = selectedMenuInfo?.data?.barraAcciones?.find((action) =>
    ["GRABAR"].includes(action?.acccaption?.toUpperCase()),
  )

  const toolbarActions = grabarAction
    ? [
        {
          label: grabarAction.acccaption,
          key: grabarAction.acccaption,
          icon: getIconComponent(grabarAction.accnameicono, grabarAction.acctipoico),
        },
      ]
    : [{ label: "Guardar", key: "GUARDAR", icon: <Save /> }]

  const isLoadingGlobal = isLoadUsuarios || isLoadAsignados || isLoadDisponibles || isSaving

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />

        <Box sx={{ mb: 2 }}>
          {toolbarActions.map((action) => (
            <Tooltip title={action.label} key={action.key}>
              <IconButton
                onClick={handleSubmit}
                disabled={isLoadingGlobal || !selectedUser}
                sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, mr: 1, background: "white" }}
              >
                {action.icon}
              </IconButton>
            </Tooltip>
          ))}
        </Box>

        <div style={{ display: "flex", justifyContent: "center", margin: "0 30px 20px 30px", fontSize: "25px" }}>
          <b>Asignación de Tipos de Solicitud a Usuarios</b>
        </div>

        <CustomBackdrop isLoading={isLoadingGlobal} />

        <Box sx={StyledRoot}>
          {/* CABECERA: SELECTOR DE USUARIO */}
          <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={listaUsuarios}
                  getOptionLabel={(option) => `${option.usrcodigo} - ${option.usrnombre || "SIN NOMBRE"}`}
                  value={selectedUser}
                  isOptionEqualToValue={(option, value) => option.usrcodigo === value?.usrcodigo}
                  onChange={(e, newValue) => {
                    setSelectedUser(newValue)
                    setSearchTerm("")
                    setLeftItems([])
                    setRightItems([])
                    setCheckedLeft([])
                    setCheckedRight([])
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Seleccione el Usuario *"
                      placeholder="Buscar usuario..."
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Paper>

          {selectedUser && (
            <Grid container spacing={3}>
              {/* CAJA IZQUIERDA: DISPONIBLES */}
              <Grid item xs={12} md={5}>
                <Paper elevation={3} sx={{ p: 2, height: "450px", display: "flex", flexDirection: "column" }}>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
                    Tipos de Solicitud Disponibles
                  </Typography>
                  <TextField
                    size="small"
                    placeholder="Buscar por código o descripción..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{ endAdornment: <SearchIcon color="action" /> }}
                    sx={{ mb: 2 }}
                  />
                  <List
                    dense
                    component="div"
                    role="list"
                    sx={{ flexGrow: 1, overflow: "auto", border: "1px solid #eee", borderRadius: 1 }}
                  >
                    {displayedLeftItems.map((tipo) => (
                      <ListItem key={tipo.tsolcodigo} button onClick={handleToggle(tipo.tsolcodigo, true)}>
                        <ListItemIcon>
                          <Checkbox checked={checkedLeft.indexOf(tipo.tsolcodigo) !== -1} tabIndex={-1} disableRipple />
                        </ListItemIcon>
                        <ListItemText
                          primary={`${tipo.tsolcodigo} - ${tipo.tsoldescripcion}`}
                          secondary={`Naturaleza: ${tipo.tsolInEg === "SOLING" ? "INGRESO" : "EGRESO"}`}
                        />
                      </ListItem>
                    ))}
                    {displayedLeftItems.length === 0 && !isLoadDisponibles && (
                      <Typography
                        variant="caption"
                        sx={{ p: 2, display: "block", textAlign: "center", color: "text.secondary" }}
                      >
                        No hay solicitudes disponibles.
                      </Typography>
                    )}
                  </List>
                </Paper>
              </Grid>

              {/* BOTONES CENTRALES */}
              <Grid
                item
                xs={12}
                md={2}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Button
                  variant="contained"
                  size="small"
                  onClick={moveRight}
                  disabled={checkedLeft.length === 0}
                  aria-label="move selected right"
                >
                  Asignar &gt;
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={moveLeft}
                  disabled={checkedRight.length === 0}
                  aria-label="move selected left"
                >
                  &lt; Revocar
                </Button>
              </Grid>

              {/* CAJA DERECHA: ASIGNADOS */}
              <Grid item xs={12} md={5}>
                <Paper
                  elevation={3}
                  sx={{ p: 2, height: "450px", display: "flex", flexDirection: "column", border: "2px solid #196C87" }}
                >
                  <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
                    Tipos de Solicitud Asignados
                  </Typography>
                  <Typography variant="caption" color="textSecondary" sx={{ mb: 2 }}>
                    Estas solicitudes están asignadas y autorizadas para el usuario actual.
                  </Typography>
                  <List
                    dense
                    component="div"
                    role="list"
                    sx={{ flexGrow: 1, overflow: "auto", border: "1px solid #eee", borderRadius: 1 }}
                  >
                    {rightItems.map((tipo) => (
                      <ListItem key={tipo.tsolcodigo} button onClick={handleToggle(tipo.tsolcodigo, false)}>
                        <ListItemIcon>
                          <Checkbox
                            checked={checkedRight.indexOf(tipo.tsolcodigo) !== -1}
                            tabIndex={-1}
                            disableRipple
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={`${tipo.tsolcodigo} - ${tipo.tsoldescripcion}`}
                          secondary={`Naturaleza: ${tipo.tsolInEg === "SOLING" ? "INGRESO" : "EGRESO"}`}
                        />
                      </ListItem>
                    ))}
                    {rightItems.length === 0 && !isLoadAsignados && (
                      <Typography
                        variant="caption"
                        sx={{ p: 2, display: "block", textAlign: "center", color: "text.secondary" }}
                      >
                        El usuario no tiene Tipos de Solicitud asignados.
                      </Typography>
                    )}
                  </List>
                </Paper>
              </Grid>
            </Grid>
          )}
        </Box>
      </div>
    </ThemeProvider>
  )
}

export default AsignacionTipoSolaUsu
